import { useEffect, useState } from "react";
import api from "@/lib/api";

const KEY = "faam-theme";

export const applyTheme = (t) => document.documentElement.classList.toggle("dark", t === "dark");
export const initTheme = () => applyTheme(localStorage.getItem(KEY) || "dark");

const hexToHsl = (hex) => {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || "");
  if (!m) return null;
  const [r, g, b] = [1, 2, 3].map((i) => parseInt(m[i], 16) / 255);
  const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2;
  if (max === min) return `0 0% ${Math.round(l * 100)}%`;
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === r ? ((g - b) / d + (g < b ? 6 : 0)) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return `${Math.round(h * 60)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

export const applyAccent = (hex) => {
  const root = document.documentElement.style;
  const hsl = hexToHsl(hex);
  ["--accent", "--ring", "--chart-1"].forEach((v) => (hsl ? root.setProperty(v, hsl) : root.removeProperty(v)));
};

export const FONT_PAIRS = {
  classic: { name: "Classico", desc: "Cormorant Garamond · Outfit · Plus Jakarta Sans", display: "'Cormorant Garamond', serif", heading: "'Outfit', sans-serif", body: "'Plus Jakarta Sans', system-ui, sans-serif",
    gf: "family=Cormorant+Garamond:ital,wght@0,500;0,600;0,700;1,400&family=Outfit:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700" },
  modern: { name: "Moderno", desc: "Sora · Sora · DM Sans", display: "'Sora', sans-serif", heading: "'Sora', sans-serif", body: "'DM Sans', system-ui, sans-serif",
    gf: "family=Sora:wght@400;500;600;700&family=DM+Sans:wght@400;500;600;700" },
  editorial: { name: "Editoriale", desc: "Playfair Display · Lora · Source Sans 3", display: "'Playfair Display', serif", heading: "'Lora', serif", body: "'Source Sans 3', system-ui, sans-serif",
    gf: "family=Playfair+Display:ital,wght@0,500;0,600;0,700;1,500&family=Lora:wght@500;600;700&family=Source+Sans+3:wght@400;500;600;700" },
  elegant: { name: "Elegante", desc: "Libre Baskerville · Manrope · Manrope", display: "'Libre Baskerville', serif", heading: "'Manrope', sans-serif", body: "'Manrope', system-ui, sans-serif",
    gf: "family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Manrope:wght@400;500;600;700" },
  geometric: { name: "Geometrico", desc: "Space Grotesk · Space Grotesk · IBM Plex Sans", display: "'Space Grotesk', sans-serif", heading: "'Space Grotesk', sans-serif", body: "'IBM Plex Sans', system-ui, sans-serif",
    gf: "family=Space+Grotesk:wght@500;600;700&family=IBM+Plex+Sans:wght@400;500;600;700" },
};

export const applyFontPair = (key) => {
  const p = FONT_PAIRS[key];
  const root = document.documentElement.style;
  if (!p || key === "classic") { ["--font-display", "--font-heading", "--font-body"].forEach((v) => root.removeProperty(v)); return; }
  let link = document.getElementById(`gf-${key}`);
  if (!link) {
    link = document.createElement("link");
    link.id = `gf-${key}`; link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?${p.gf}&display=swap`;
    document.head.appendChild(link);
  }
  root.setProperty("--font-display", p.display);
  root.setProperty("--font-heading", p.heading);
  root.setProperty("--font-body", p.body);
};

export const useTheme = (user) => {
  const [theme, setTheme] = useState(() => localStorage.getItem(KEY) || "dark");
  useEffect(() => { applyTheme(theme); localStorage.setItem(KEY, theme); }, [theme]);
  useEffect(() => { if (user?.theme && user.theme !== theme) setTheme(user.theme); }, [user?.theme]); // eslint-disable-line
  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    if (user) api.patch("/auth/me/preferences", { theme: next }).catch(() => {});
  };
  return { dark: theme === "dark", toggleTheme };
};
